/**
 * Glyph geometry for a character span in a page's extracted text.
 *
 * Extracted verbatim from `keywordRedactionsFromItems` (VE-24948 / U5) so the
 * lexicon-apply path can reuse the SAME geometry as phrase redaction instead of
 * growing a second implementation. `keywordRedactionsFromItems` now calls these
 * functions; its behaviour is unchanged and its existing tests are the proof.
 *
 * ── Why this, and NOT the keyword-search saga's mapping ──────────────────────
 *
 * `sagas/keywordSearch.ts` also maps offsets to a rectangle, and reusing that
 * would have been the obvious move. It would also have been wrong. That mapping
 * produces a single UNION box across every item the match covers, plus a 2-unit
 * pad, and its own file records that it is "only accurate enough for the
 * highlight overlay" — keyword *redactions* deliberately do not use it.
 *
 * The difference matters because lexicon detections drive burn-in. For a match
 * that WRAPS ACROSS LINES, a union box covers the whole rectangle between the
 * two rows, so it would redact unrelated text sitting between them. That is
 * over-redaction on the product's core failure path, and it is exactly what the
 * per-row split below exists to prevent (VE-27405).
 *
 * ── What these produce ───────────────────────────────────────────────────────
 *
 * The NEUTRAL (exact-glyph) box, with NO baked pad: the roomier visual box and
 * the inset removal box are derived downstream (`GLYPH_REDACTION_*` in
 * `helpers.ts`). Baking a pad here would stack with the visual pad and render
 * boxes taller and wider than the search highlight (VE-27405).
 *
 * Geometry comes from MuPDF's own per-glyph item boxes — the same geometry the
 * visible page is rendered from — which is why it stays aligned on both rendered
 * and virtualized pages.
 */

import { BoundingBox } from './models';

/** The per-item geometry these functions need. Structural, so callers may pass more. */
export interface GlyphItem {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly length: number;
}

/** One item's character range in the concatenated page text. */
export interface ItemCharRange {
  readonly start: number;
  readonly end: number;
  readonly idx: number;
}

/**
 * Character range of each item in the concatenated page text.
 *
 * `buildPageText` puts exactly ONE separator char (a space or a zero-width
 * seam) between items, so each item occupies `length` chars followed by 1
 * separator. The keyword-search saga uses the identical convention — if that
 * ever diverges, offsets silently mis-map to glyphs, so the two must stay in
 * step.
 */
export function itemCharRanges(
  items: ReadonlyArray<GlyphItem | undefined>
): ItemCharRange[] {
  const ranges: ItemCharRange[] = [];
  let offset = 0;
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (!it) {
      continue;
    }
    ranges.push({ start: offset, end: offset + it.length, idx: i });
    offset += it.length + 1;
  }
  return ranges;
}

/**
 * Per-row exact-glyph boxes covering one character span.
 *
 * Split PER ROW, never unioned: items arrive in reading order, so a wrap shows
 * up as the item top jumping down by about a line. A single union box would
 * swallow the rectangle between the two rows and redact unrelated text.
 *
 * Returns an empty array when the span covers no glyphs (a separator-only
 * overlap, or no items) — a valid result, not an error.
 */
export function rowBoxesForSpan(
  span: { readonly startIdx: number; readonly endIdx: number },
  items: ReadonlyArray<GlyphItem | undefined>,
  ranges: readonly ItemCharRange[] = itemCharRanges(items)
): BoundingBox[] {
  const { startIdx, endIdx } = span;
  const boxes: BoundingBox[] = [];

  let row: { minX: number; minY: number; maxX: number; maxY: number } | null =
    null;

  const flushRow = () => {
    if (row && row.maxX > row.minX) {
      boxes.push({
        x: row.minX,
        y: row.minY,
        width: row.maxX - row.minX,
        height: row.maxY - row.minY,
      });
    }
    row = null;
  };

  // `ranges` are sorted and disjoint: bisect to the first overlap, stop past
  // the span. A full scan per span was O(matches x items) (VE-29502).
  let lo = 0;
  let hi = ranges.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (ranges[mid]!.end <= startIdx) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  for (let i = lo; i < ranges.length; i++) {
    const r = ranges[i]!;
    if (r.start >= endIdx) {
      break;
    }
    // Skip items that don't overlap the span's char range.
    if (r.end <= startIdx) {
      continue;
    }
    const item = items[r.idx];
    if (!item) {
      continue;
    }
    const charWidth = item.length > 0 ? item.width / item.length : 0;
    const from = Math.max(startIdx, r.start) - r.start;
    const to = Math.min(endIdx, r.end) - r.start;
    const x0 = item.x + from * charWidth;
    const x1 = item.x + to * charWidth;
    const yTop = item.y;
    const yBot = item.y + item.height;
    const h = item.height > 0 ? item.height : 12;
    if (row && Math.abs(yTop - row.minY) <= h * 0.5) {
      // Same row as the run so far.
      row.minX = Math.min(row.minX, x0);
      row.maxX = Math.max(row.maxX, x1);
      row.minY = Math.min(row.minY, yTop);
      row.maxY = Math.max(row.maxY, yBot);
    } else {
      // New row (or first item) — emit the previous row's box and start fresh.
      flushRow();
      row = { minX: x0, minY: yTop, maxX: x1, maxY: yBot };
    }
  }
  flushRow();

  return boxes;
}
